import { ReactNode, useEffect, useRef, useState } from 'react';
import { Button } from './ui/Button';
import { COLORS, INPUT_STYLE } from '../constants/theme';

interface ConfirmModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  title: string;
  children: ReactNode;
  confirmText?: string;
  cancelText?: string;
  /** When set, requires the literal keyword to be typed into a text input before Confirm enables. */
  requireTypedConfirmation?: { keyword: string };
  /** Additive: forces Confirm disabled regardless of typed-confirmation state (e.g. invalid embedded input). */
  confirmDisabled?: boolean;
}

export function ConfirmModal({
  isOpen,
  onConfirm,
  onCancel,
  title,
  children,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  requireTypedConfirmation,
  confirmDisabled,
}: ConfirmModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<Element | null>(null);
  const [typedValue, setTypedValue] = useState('');
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  // Focus trap + Escape-to-close + focus-return, mirroring LoginModal.tsx's pattern.
  useEffect(() => {
    if (!isOpen) return;

    triggerRef.current = document.activeElement;
    setTypedValue('');

    const focusable = modalRef.current?.querySelectorAll<HTMLElement>(
      'input, button:not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])'
    );
    focusable?.[0]?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancelRef.current();
        return;
      }
      if (e.key === 'Tab' && modalRef.current) {
        const focusableEls = modalRef.current.querySelectorAll<HTMLElement>(
          'input, button:not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusableEls.length === 0) return;
        const first = focusableEls[0];
        const last = focusableEls[focusableEls.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (triggerRef.current instanceof HTMLElement) {
        triggerRef.current.focus();
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const typedConfirmationSatisfied =
    !requireTypedConfirmation || typedValue.trim() === requireTypedConfirmation.keyword;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-title"
        className="rounded-lg shadow-xl max-w-md w-full mx-4"
        style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
      >
        <div className="p-6">
          <h3
            id="confirm-modal-title"
            className="text-lg font-semibold mb-4"
            style={{ color: COLORS.text.primary }}
          >
            {title}
          </h3>
          {/* SF-165: additive scroll container — a long raze/build confirmation list (>10-acre
           *  threshold triggers far more often now that a single percent-stepper tap can queue
           *  20+ acres) could otherwise push Confirm/Cancel off a 375×667 viewport. Only affects
           *  content already tall enough to need it; every other ConfirmModal consumer unaffected. */}
          <div className="mb-6 max-h-[80vh] overflow-y-auto" style={{ color: COLORS.text.primary }}>{children}</div>

          {requireTypedConfirmation && (
            <div className="mb-6">
              <label
                htmlFor="confirm-modal-typed-input"
                className="block mb-1.5 text-sm"
                style={{ color: COLORS.text.tertiary }}
              >
                Type <strong>{requireTypedConfirmation.keyword}</strong> to confirm
              </label>
              <input
                id="confirm-modal-typed-input"
                type="text"
                autoComplete="off"
                value={typedValue}
                onChange={(e) => setTypedValue(e.target.value)}
                className="w-full px-3 py-2 rounded focus:outline-none"
                style={INPUT_STYLE}
              />
            </div>
          )}

          <div className="flex gap-3 justify-end">
            <Button
              variant="secondary"
              onClick={onCancel}
            >
              {cancelText}
            </Button>
            <Button onClick={onConfirm} disabled={confirmDisabled || !typedConfirmationSatisfied}>
              {confirmText}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
